import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface IFloorplanRegisterState {
  selectedFloor: string;
  selectedFloorSpaceId: string;
  selectedFloorSpaceIds: string[];
  clickedRoomNumberInModal: string | null;
  roomSearchText: string;
  floorSearchText: string;
  showOnlyFloorsWithIssues: boolean;
  showLengthItemsInSelectedRoom: boolean;
  floorplanRotation: number;
}

export interface IFloorplanState {
  byIssueBoardId: Record<string, IFloorplanRegisterState>;
}

type FloorplanPayload<T extends object = object> = T & { issueBoardId: string };

const getFloorplanRotationStorageKey = (issueBoardId: string) => `floorplanRotation:${issueBoardId}`;

const getInitFloorplanRotation = (issueBoardId: string) => {
  if (typeof localStorage === "undefined") {
    return 270;
  }

  const stored = localStorage.getItem(getFloorplanRotationStorageKey(issueBoardId));
  const parsed = stored != null ? Number(stored) : NaN;
  return Number.isFinite(parsed) ? parsed : 270;
};

export const getInitialFloorplanRegisterState = (issueBoardId: string): IFloorplanRegisterState => ({
  selectedFloor: "",
  selectedFloorSpaceId: "",
  selectedFloorSpaceIds: [],
  clickedRoomNumberInModal: null,
  roomSearchText: "",
  floorSearchText: "",
  showOnlyFloorsWithIssues: false,
  showLengthItemsInSelectedRoom: false,
  floorplanRotation: getInitFloorplanRotation(issueBoardId),
});

const getRegisterState = (state: IFloorplanState, issueBoardId: string) => {
  state.byIssueBoardId ??= {};
  state.byIssueBoardId[issueBoardId] ??= getInitialFloorplanRegisterState(issueBoardId);
  return state.byIssueBoardId[issueBoardId];
};

const getInitialFloorplanState = (): IFloorplanState => ({
  byIssueBoardId: {},
});

export const floorplanSlice = createSlice({
  name: "floorplan",
  initialState: getInitialFloorplanState(),
  reducers: {
    setSelectedFloorInRedux: (state, action: PayloadAction<FloorplanPayload<{ selectedFloor: string }>>) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      registerState.selectedFloor = action.payload.selectedFloor;
      registerState.selectedFloorSpaceId = "";
      registerState.selectedFloorSpaceIds = [];
      registerState.clickedRoomNumberInModal = null;
    },
    setSelectedFloorSpaceIdInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ selectedFloorSpaceId: string }>>,
    ) => {
      getRegisterState(state, action.payload.issueBoardId).selectedFloorSpaceId = action.payload.selectedFloorSpaceId;
    },
    setSelectedFloorSpaceIdsInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ selectedFloorSpaceIds: string[] }>>,
    ) => {
      getRegisterState(state, action.payload.issueBoardId).selectedFloorSpaceIds = action.payload.selectedFloorSpaceIds;
    },
    addSelectedFloorSpaceIdInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ selectedFloorSpaceId: string }>>,
    ) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      if (!registerState.selectedFloorSpaceIds.includes(action.payload.selectedFloorSpaceId)) {
        registerState.selectedFloorSpaceIds.push(action.payload.selectedFloorSpaceId);
      }
    },
    removeSelectedFloorSpaceIdInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ selectedFloorSpaceId: string }>>,
    ) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      registerState.selectedFloorSpaceIds = registerState.selectedFloorSpaceIds.filter(
        (spaceId) => spaceId !== action.payload.selectedFloorSpaceId,
      );
    },
    toggleSelectedFloorSpaceIdInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ selectedFloorSpaceId: string }>>,
    ) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      const spaceId = action.payload.selectedFloorSpaceId;
      const selectedFloorSpaceIds = new Set(registerState.selectedFloorSpaceIds);

      if (registerState.selectedFloorSpaceId !== "" && registerState.selectedFloorSpaceId !== spaceId) {
        selectedFloorSpaceIds.add(registerState.selectedFloorSpaceId);
      }

      if (selectedFloorSpaceIds.has(spaceId)) {
        selectedFloorSpaceIds.delete(spaceId);
        registerState.selectedFloorSpaceIds = Array.from(selectedFloorSpaceIds);
        return;
      }

      selectedFloorSpaceIds.add(spaceId);
      registerState.selectedFloorSpaceIds = Array.from(selectedFloorSpaceIds);
    },
    clearSelectedFloorSpaceIdsInRedux: (state, action: PayloadAction<FloorplanPayload>) => {
      getRegisterState(state, action.payload.issueBoardId).selectedFloorSpaceIds = [];
    },
    clearSelectedFloorSpacesInRedux: (state, action: PayloadAction<FloorplanPayload>) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      registerState.selectedFloorSpaceId = "";
      registerState.selectedFloorSpaceIds = [];
      registerState.clickedRoomNumberInModal = null;
    },
    setClickedRoomNumberInModalInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ clickedRoomNumberInModal: string | null }>>,
    ) => {
      getRegisterState(state, action.payload.issueBoardId).clickedRoomNumberInModal =
        action.payload.clickedRoomNumberInModal;
    },
    setRoomSearchTextInRedux: (state, action: PayloadAction<FloorplanPayload<{ roomSearchText: string }>>) => {
      getRegisterState(state, action.payload.issueBoardId).roomSearchText = action.payload.roomSearchText;
    },
    setFloorSearchTextInRedux: (state, action: PayloadAction<FloorplanPayload<{ floorSearchText: string }>>) => {
      getRegisterState(state, action.payload.issueBoardId).floorSearchText = action.payload.floorSearchText;
    },
    setShowOnlyFloorsWithIssuesInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ showOnlyFloorsWithIssues: boolean }>>,
    ) => {
      getRegisterState(state, action.payload.issueBoardId).showOnlyFloorsWithIssues =
        action.payload.showOnlyFloorsWithIssues;
    },
    setShowLengthItemsInSelectedRoomInRedux: (
      state,
      action: PayloadAction<FloorplanPayload<{ showLengthItemsInSelectedRoom: boolean }>>,
    ) => {
      getRegisterState(state, action.payload.issueBoardId).showLengthItemsInSelectedRoom =
        action.payload.showLengthItemsInSelectedRoom;
    },
    setFloorplanRotationInRedux: (state, action: PayloadAction<FloorplanPayload<{ floorplanRotation: number }>>) => {
      const registerState = getRegisterState(state, action.payload.issueBoardId);
      registerState.floorplanRotation = action.payload.floorplanRotation;
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          getFloorplanRotationStorageKey(action.payload.issueBoardId),
          String(action.payload.floorplanRotation),
        );
      }
    },
  },
});

export const {
  setSelectedFloorInRedux,
  setSelectedFloorSpaceIdInRedux,
  setSelectedFloorSpaceIdsInRedux,
  addSelectedFloorSpaceIdInRedux,
  removeSelectedFloorSpaceIdInRedux,
  toggleSelectedFloorSpaceIdInRedux,
  clearSelectedFloorSpaceIdsInRedux,
  clearSelectedFloorSpacesInRedux,
  setClickedRoomNumberInModalInRedux,
  setRoomSearchTextInRedux,
  setFloorSearchTextInRedux,
  setShowOnlyFloorsWithIssuesInRedux,
  setShowLengthItemsInSelectedRoomInRedux,
  setFloorplanRotationInRedux,
} = floorplanSlice.actions;

export default floorplanSlice.reducer;
